import { useState, useEffect, useRef } from "react"
import { useParams, Link, Navigate } from "react-router-dom"
import { ArrowLeft, User, Clock, ThumbsUp, ThumbsDown } from "lucide-react"
import { getArticleBySlug, getRelatedArticles } from "@/data/helpData"
import ArticleCard from "../../components/ArticleCard"
import HelpSidebar from "../../components/HelpSidebar"
import MarkdownRenderer from "../../components/MarkdownRenderer"

interface TableOfContentsItem {
  id: string
  text: string
  level: number
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? getArticleBySlug(slug) : undefined
  const articleContentRef = useRef<HTMLDivElement>(null)
  const [toc, setToc] = useState<TableOfContentsItem[]>([])

  useEffect(() => {
    if (article && articleContentRef.current) {
      // Generate table of contents from headings
      const timer = setTimeout(() => {
        const contentElement = articleContentRef.current
        if (contentElement) {
          const headings = Array.from(contentElement.querySelectorAll('h2, h3'))
          const generatedToc = headings.map((h, index) => {
            const id = h.id || `heading-${slug}-${index}`
            h.id = id
            return {
              id: id,
              text: h.textContent || '',
              level: parseInt(h.tagName.substring(1), 10)
            }
          })
          setToc(generatedToc)
        }
      }, 150)
      
      return () => clearTimeout(timer)
    }
  }, [article, slug])

  if (!article) {
    return <Navigate to="/help/articles" replace />
  }

  const relatedArticles = getRelatedArticles(article.id, 2)

  const handleTocClick = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault()
    const element = document.getElementById(itemId)
    if (element) {
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Sidebar Navigation */}
        <div className="hidden md:block w-72 shrink-0 border-r border-gray-200 dark:border-gray-800 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto p-4">
          <div className="py-4">
            <h2 className="text-lg font-semibold mb-4 pl-3 text-purple-900 dark:text-purple-300">
              Navigation
            </h2>
            <HelpSidebar currentPath={`/help/articles/${slug}`} />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 xl:col-span-9">
                {/* Breadcrumb */}
                <div className="flex items-center mb-6">
                  <Link
                    to="/help/articles"
                    className="inline-flex items-center text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Articles
                  </Link>
                </div>

                {/* Article Content */}
                <article className="bg-white dark:bg-gray-950 p-6 sm:p-8 md:p-10 rounded-xl shadow-xl border border-gray-200 dark:border-gray-800">
                  {/* Article Header */}
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mb-4 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                    {article.category}
                  </span>
                  
                  <h1 className="text-3xl lg:text-4xl font-extrabold text-purple-900 dark:text-purple-200 mb-5 leading-tight">
                    {article.title}
                  </h1>
                  
                  <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mb-8 gap-x-4 gap-y-2">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{article.readTime}</span>
                    </div>
                    <span>{article.date}</span>
                  </div>

                  {/* Article Body */}
                  <div ref={articleContentRef} className="mb-10">
                    <MarkdownRenderer
                      content={article.content}
                      className="prose prose-purple dark:prose-invert max-w-none prose-lg"
                    />
                  </div>

                  {/* Feedback Section */}
                  <div className="bg-purple-50 dark:bg-purple-900/30 p-6 rounded-lg mb-8 border border-purple-100 dark:border-purple-800 shadow-sm">
                    <h3 className="text-lg font-medium text-purple-900 dark:text-purple-300 mb-4">
                      Was this article helpful?
                    </h3>
                    <div className="flex space-x-4">
                      <button className="flex items-center px-4 py-2 border border-purple-300 dark:border-purple-700 rounded-lg text-sm font-medium text-purple-800 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors">
                        <ThumbsUp className="h-4 w-4 mr-2" />
                        Yes, it helped
                      </button>
                      <button className="flex items-center px-4 py-2 border border-purple-300 dark:border-purple-700 rounded-lg text-sm font-medium text-purple-800 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors">
                        <ThumbsDown className="h-4 w-4 mr-2" />
                        No, I need more info
                      </button>
                    </div>
                  </div>
                </article>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <div className="mt-12">
                    <h2 className="text-2xl font-semibold text-purple-900 dark:text-purple-300 mb-6">
                      Related Articles
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {relatedArticles.map(relatedArticle => (
                        <ArticleCard key={relatedArticle.id} article={relatedArticle} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Table of Contents Sidebar */}
              <aside className="lg:col-span-4 xl:col-span-3">
                <div className="sticky top-24 space-y-8">
                  {/* Table of Contents */}
                  <div className="bg-white dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200 dark:border-gray-800/70 p-6 rounded-xl shadow-lg">
                    <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-4">
                      Table of Contents
                    </h3>
                    <nav className="space-y-2 max-h-[calc(100vh-20rem)] overflow-y-auto">
                      {toc.length > 0 ? (
                        toc.map(item => (
                          <a
                            key={item.id}
                            href={`#${item.id}`}
                            onClick={(e) => handleTocClick(e, item.id)}
                            className={`block text-sm hover:text-purple-800 dark:hover:text-purple-300 hover:underline transition-colors ${
                              item.level === 3 
                                ? 'ml-3 text-gray-600 dark:text-gray-400' 
                                : 'font-medium text-purple-700 dark:text-purple-400'
                            } ${
                              item.level === 2
                                ? 'font-semibold text-purple-800 dark:text-purple-300'
                                : 'text-purple-700 dark:text-purple-400'
                            }`}
                          >
                            {item.text}
                          </a>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No sections found.</p>
                      )}
                    </nav>
                  </div>

                  {/* Need Help */}
                  <div className="bg-white dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200 dark:border-gray-800/70 p-6 rounded-xl shadow-lg">
                    <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300 mb-3">
                      Need Help?
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                      Can't find what you're looking for? Our support team is ready to assist you.
                    </p>
                    <button className="w-full inline-flex items-center justify-center rounded-lg text-sm font-medium h-10 px-4 py-2 bg-purple-600 text-purple-50 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 transition-colors">
                      Contact Support
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}